import { useState, type ReactElement } from "react";
import { Col, Flex, Row } from "antd";
import useAxios from "../../../shared/custom-hooks/useAxios";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { EditButton } from "../../../shared/layout/EditButton";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { type ProtocolResponse } from "../../new-trail/types";
import { nutrientTypeOptions, targetsLabels } from "../constants";
import { normalizeNutrientCode } from "../utils/nutrientNormalization";
import BiologicalProductSummaryCard from "./Summary/BiologicalProductSummaryCard";
import { Loader } from "../../../shared/components/Loader";
import { useProtocolsRouter } from "../../../shared/custom-hooks/useProtocolsRouter";
import "./summary.less";

interface SeedDensityRange {
  min?: number | null;
  max?: number | null;
}

interface SeedsDetailsWithOptions {
  global_density_range?: boolean;
  density_range?: SeedDensityRange | null;
  density_options?: number[] | null;
  measure_unit?: string;
  products?: Array<{
    variety?: string;
    density_range?: SeedDensityRange | null;
    density_options?: number[] | null;
  }>;
}

const normalizeDensityOptions = (opts: unknown): number[] => {
  if (!Array.isArray(opts)) return [];
  const nums = (opts as unknown[]).filter(
    (n): n is number => typeof n === "number" && Number.isFinite(n)
  );
  return Array.from(new Set(nums)).sort((a, b) => a - b);
};

const numberOrNull = (val: unknown): number | null =>
  typeof val === "number" && Number.isFinite(val) ? val : null;

const formatRangeText = (min: unknown, max: unknown, unit: string): string => {
  const minNum = numberOrNull(min);
  const maxNum = numberOrNull(max);
  if (minNum === null || maxNum === null) return "-";
  return `${minNum} - ${maxNum} ${unit}`.trim();
};

const renderDensityOptionChips = (opts: unknown): JSX.Element => {
  const normalized = normalizeDensityOptions(opts);
  if (normalized.length === 0) return <span>-</span>;

  return (
    <span className="density-options">
      {normalized.map((val) => (
        <span key={val} className="density-option-chip">
          {val}
        </span>
      ))}
    </span>
  );
};

const SummaryComponent = (): ReactElement => {
  const { t } = useTranslation();
  const { router: protocolsRouter, workspaceId, orgId } = useProtocolsRouter();
  const [searchParams] = useSearchParams();
  const [expandedObjectives, setExpandedObjectives] = useState<
    Record<number, boolean>
  >({});
  const protocolId: string | null = searchParams.get("protocol_id");
  let data: ProtocolResponse | null = null;

  const headers = {
    "org-id": orgId ?? "",
    "workspace-id": workspaceId ?? "",
  };

  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const { loading, response, error } = useAxios(
    `/protocol/${protocolId ?? ""}`,
    false,
    headers
  );

  data = response;

  const findNutrientLabel = (value: string): string => {
    // Normalize input to handle legacy/dynamic formats from backend
    const normalizedValue = normalizeNutrientCode(value);
    if (!normalizedValue) return "";

    for (const category of nutrientTypeOptions) {
      const nutrient = category?.options.find(
        (option: { value: string; label: string }) =>
          option?.value === normalizedValue
      );
      if (nutrient) {
        return nutrient?.label;
      }
    }
    return "";
  };

  if (error) {
    return (
      <div data-testid="error-message">
        {formatTranslation(t("createProtocol.summary.errorFetchingSummary"))}
      </div>
    );
  }

  const getSeedsHasOptions = (
    seeds: SeedsDetailsWithOptions | null
  ): boolean => {
    if (!seeds) return false;

    const globalHasOptions =
      normalizeDensityOptions(seeds.density_options).length > 0;

    const productHasOptions =
      Array.isArray(seeds.products) &&
      seeds.products.some(
        (p) => normalizeDensityOptions(p?.density_options).length > 0
      );

    return globalHasOptions || productHasOptions;
  };

  const toggleObjective = (index: number): void => {
    setExpandedObjectives((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div className="summary-component">
      {loading && (
        <div data-testid={"loader"}>
          <Loader mask message="" />
        </div>
      )}
      {data && protocolId && (
        <>
          <Flex
            gap={32}
            justify="space-between"
            align="start"
            className="summay-content"
          >
            <Flex
              gap={16}
              align="center"
              justify="start"
              className="summary-content-title"
            >
              <div className="summary-card-title">
                {formatTranslation(t("createProtocol.summary.title1"))}
              </div>
              <EditButton
                className="summary"
                from="protocolType"
                onClick={() => {
                  protocolsRouter.goToProtocolsEditPage(
                    createSearchParams({ protocol_id: protocolId }).toString(),
                    protocolId,
                    "trial-type"
                  );
                }}
              />
            </Flex>
            <Flex
              vertical
              gap={4}
              align="start"
              justify="center"
              className="summary-details-info-container"
            >
              <label className="summary-card-static-text">
                {formatTranslation(t("createProtocol.summary.trialSelected"))}
              </label>
              <Flex gap={4} align={"center"} justify="start">
                {data?.protocol_type.map((type, index) => {
                  let colorType = "";
                  let typeName = "";
                  if (type === "seed") {
                    colorType = "seedType";
                    typeName = formatTranslation(t("trialtypes.label.seed"));
                  } else if (type === "nutrient") {
                    colorType = "nutrientType";
                    typeName = formatTranslation(
                      t("trialtypes.label.fertiliser")
                    );
                  } else {
                    colorType = "cropProtectionType";
                    typeName = formatTranslation(
                      t("trialtypes.label.cropProtection")
                    );
                  }
                  return (
                    <p
                      className={`${colorType} summary-card-dynamic-text`}
                      key={index}
                    >
                      {typeName}
                    </p>
                  );
                })}
              </Flex>
            </Flex>
          </Flex>

          <Flex
            gap={32}
            justify="space-between"
            align="start"
            className="summay-content"
          >
            <Flex
              gap={16}
              align="center"
              justify="start"
              className="summary-content-title"
            >
              <div className="summary-card-title">
                {formatTranslation(t("createProtocol.basicInfo.rule"))}
              </div>
              <EditButton
                className="summary"
                onClick={() => {
                  protocolsRouter.goToProtocolsEditPage(
                    createSearchParams({ protocol_id: protocolId }).toString(),
                    protocolId,
                    "rules"
                  );
                }}
              />
            </Flex>

            <Flex
              vertical
              gap={24}
              justify="start"
              className="summary-details-info-container"
            >
              <Flex
                vertical
                gap={16}
                align="start"
                justify="start"
                className="summary-card-protocol-info protocol-details"
              >
                <label className="summary-card-static-title">
                  {formatTranslation(
                    t("createProtocol.basicInfo.protocolDetails")
                  )}
                </label>

                <Flex
                  gap={16}
                  align={"center"}
                  justify="space-between"
                  className="summary-card-protocol-info"
                >
                  <Flex
                    vertical
                    gap={4}
                    justify="center"
                    className="summary-card-protocol-info"
                  >
                    <label className="summary-card-static-text">
                      {formatTranslation(
                        t("createProtocol.basicInfo.protocolName")
                      )}
                    </label>
                    <p className="summary-card-dynamic-protocol-text">
                      {data?.name}
                    </p>
                  </Flex>
                  <Flex
                    vertical
                    gap={4}
                    justify="center"
                    className="summary-card-protocol-info"
                  >
                    <label className="summary-card-static-text">
                      {formatTranslation(
                        t("createProtocol.objectives.objectiveItemDescription")
                      )}
                    </label>
                    <p className="summary-card-dynamic-protocol-text">
                      {data?.description}
                    </p>
                  </Flex>
                </Flex>

                <Flex
                  gap={16}
                  align={"center"}
                  justify="space-between"
                  className="summary-card-protocol-info"
                >
                  <Flex
                    vertical
                    gap={4}
                    justify="center"
                    className="summary-card-protocol-info"
                  >
                    <label className="summary-card-static-text">
                      {formatTranslation(
                        t("createProtocol.basicInfo.protocolCountry")
                      )}
                    </label>
                    <p className="summary-card-dynamic-protocol-text">
                      {data?.crop_details?.countries?.[0] ||
                        data?.crop_details?.country}
                    </p>
                  </Flex>
                  <Flex
                    vertical
                    gap={4}
                    justify="center"
                    className="summary-card-protocol-info"
                  >
                    <label className="summary-card-static-text">
                      {formatTranslation(
                        t("createProtocol.basicInfo.protocolCrop")
                      )}
                    </label>
                    <p className="summary-card-dynamic-protocol-text">
                      {data?.crop}
                    </p>
                  </Flex>

                  {data?.trial_application_method === "plot" ? (
                    <>
                      <Flex
                        vertical
                        gap={4}
                        justify="center"
                        className="summary-card-protocol-info"
                      >
                        <label className="summary-card-static-text">
                          {formatTranslation(
                            t("createProtocol.basicInfo.plotMinimumSize")
                          )}
                        </label>
                        <p className="summary-card-dynamic-protocol-text">
                          {data?.plot_minimum_size?.width} x{" "}
                          {data?.plot_minimum_size?.length} m
                        </p>
                      </Flex>
                      <Flex
                        vertical
                        gap={4}
                        justify="center"
                        className="summary-card-protocol-info"
                      >
                        <label className="summary-card-static-text">
                          {formatTranslation(
                            t("createProtocol.basicInfo.plotRecommendedSize")
                          )}
                        </label>
                        <p className="summary-card-dynamic-protocol-text">
                          {data?.plot_recommended_size?.width} x{" "}
                          {data?.plot_recommended_size?.length} m
                        </p>
                      </Flex>
                    </>
                  ) : data?.trial_application_method === "strip" ? (
                    <>
                      <Flex
                        vertical
                        gap={4}
                        justify="center"
                        className="summary-card-protocol-info"
                      >
                        <label className="summary-card-static-text">
                          {formatTranslation(
                            t("createProtocol.basicInfo.stripMinSize")
                          )}
                        </label>
                        <p className="summary-card-dynamic-protocol-text">
                          {data?.strip_size?.min_width}
                        </p>
                      </Flex>
                      <Flex
                        vertical
                        gap={4}
                        justify="center"
                        className="summary-card-protocol-info"
                      >
                        <label className="summary-card-static-text">
                          {formatTranslation(
                            t("createProtocol.basicInfo.stripMaxSize")
                          )}
                        </label>
                        <p className="summary-card-dynamic-protocol-text">
                          {data?.strip_size?.max_width}
                        </p>
                      </Flex>
                    </>
                  ) : null}
                </Flex>
              </Flex>

              <Flex
                vertical
                gap={16}
                align="start"
                justify="center"
                className="summary-card-protocol-info"
              >
                <label className="summary-card-static-title">
                  {formatTranslation(t("createProtocol.objectives.title"))}
                </label>
                {data?.objectives?.map((objective: any, index: number) => {
                  const description: string = String(
                    objective?.description ?? ""
                  );
                  const isLong = description.length > 100;
                  const isExpanded = !!expandedObjectives[index];
                  const shortText = description.slice(0, 100) + "...";

                  return (
                    <Flex
                      gap={16}
                      align={"start"}
                      justify="space-between"
                      className="summary-card-protocol-info"
                      key={index}
                    >
                      <Flex
                        vertical
                        gap={4}
                        justify="center"
                        className="summary-card-protocol-info"
                      >
                        <label className="summary-card-static-text">
                          {formatTranslation(
                            t("createProtocol.objectives.objectiveName")
                          )}
                        </label>
                        <p className="summary-card-dynamic-protocol-text">
                          {objective?.name}
                        </p>
                      </Flex>
                      <Flex
                        vertical
                        gap={4}
                        justify="center"
                        className="summary-card-protocol-info"
                      >
                        <label className="summary-card-static-text">
                          {formatTranslation(
                            t("createProtocol.objectives.objectiveItemTarget")
                          )}
                        </label>
                        <p className="summary-card-dynamic-protocol-text">
                          {objective?.targets
                            .map((target: string) =>
                              formatTranslation(
                                t(targetsLabels[target] || target)
                              )
                            )
                            .join(", ")}
                        </p>
                      </Flex>
                      <Flex
                        vertical
                        gap={4}
                        justify="center"
                        className="summary-card-protocol-info"
                      >
                        <label className="summary-card-static-text">
                          {formatTranslation(
                            t(
                              "createProtocol.objectives.objectiveItemDescription"
                            )
                          )}
                        </label>
                        <p className="summary-card-dynamic-protocol-text">
                          {isLong
                            ? isExpanded
                              ? description
                              : shortText
                            : description}
                          {isLong && (
                            <button
                              type="button"
                              onClick={() => {
                                toggleObjective(index);
                              }}
                              className="summary-objective-showmore-btn"
                            >
                              {isExpanded ? "Show less" : "Show more"}
                            </button>
                          )}
                        </p>
                      </Flex>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>
          </Flex>

          {data?.protocol_type.includes("seed") && (
            <Flex
              gap={32}
              justify="space-between"
              align="start"
              className="summay-content"
            >
              <Flex
                gap={16}
                align="center"
                justify="start"
                className="summary-content-title"
              >
                <div className="summary-card-title">
                  {formatTranslation(t("plotDetails.seeds"))}
                </div>
                <EditButton
                  className="summary"
                  from="seeds"
                  onClick={() => {
                    protocolsRouter.goToProtocolsEditPage(
                      createSearchParams({
                        protocol_id: protocolId,
                        type: "seed",
                      }).toString(),
                      protocolId,
                      "products"
                    );
                  }}
                />
              </Flex>

              <div className="summary-details-info-container">
                <Row className="products-header">
                  <Col span={12}>
                    <p className="summary-card-static-text-seeds">
                      {formatTranslation(
                        t("createProtocol.products.rightTilte")
                      )}
                    </p>
                  </Col>
                  <Col span={12}>
                    {(() => {
                      const seeds =
                        (data?.product_details
                          ?.seeds as unknown as SeedsDetailsWithOptions) ??
                        null;

                      const unit: string = seeds?.measure_unit ?? "";
                      const seedsHasOptions = getSeedsHasOptions(seeds);

                      return (
                        <p className="summary-card-static-text-seeds">
                          {formatTranslation(
                            t("createProtocol.products.densityRange")
                          )}
                          {seedsHasOptions && unit ? ` ${unit}` : ""}
                        </p>
                      );
                    })()}
                  </Col>
                </Row>

                {data?.product_details?.seeds ? (
                  <>
                    {(
                      data?.product_details
                        ?.seeds as unknown as SeedsDetailsWithOptions
                    )?.products?.map((product, index) => {
                      const seeds = data?.product_details
                        ?.seeds as unknown as SeedsDetailsWithOptions;

                      const unit: string = seeds?.measure_unit ?? "";
                      const showGlobal = Boolean(seeds?.global_density_range);

                      const globalHasOptions =
                        normalizeDensityOptions(seeds?.density_options).length >
                        0;
                      const productHasOptions =
                        normalizeDensityOptions(product?.density_options)
                          .length > 0;

                      return (
                        <Row key={index} className="products-row">
                          <Col span={12}>
                            <p className="summary-card-dynamic-protocol-text">
                              {product?.variety}
                            </p>
                          </Col>
                          <Col span={12}>
                            <div className="summary-card-dynamic-protocol-text">
                              {showGlobal
                                ? globalHasOptions
                                  ? renderDensityOptionChips(
                                      seeds?.density_options
                                    )
                                  : formatRangeText(
                                      seeds?.density_range?.min,
                                      seeds?.density_range?.max,
                                      unit
                                    )
                                : productHasOptions
                                ? renderDensityOptionChips(
                                    product?.density_options
                                  )
                                : formatRangeText(
                                    product?.density_range?.min,
                                    product?.density_range?.max,
                                    unit
                                  )}
                            </div>
                          </Col>
                        </Row>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {formatTranslation(
                      t("createProtocol.summary.productDataNotFound")
                    )}
                  </>
                )}
              </div>
            </Flex>
          )}

          {data?.protocol_type.includes("biological") && (
            <BiologicalProductSummaryCard
              biologicals={
                data?.product_details?.biologicals
                  ? data?.product_details?.biologicals
                  : null
              }
            />
          )}

          {data?.protocol_type.includes("nutrient") && (
            <Flex
              gap={32}
              justify="space-between"
              align="start"
              className="summay-content"
            >
              <Flex
                gap={16}
                align="center"
                justify="start"
                className="summary-content-title"
              >
                <div className="summary-card-title">
                  {formatTranslation(
                    t("createProtocol.products.nutrients.header")
                  )}
                </div>
                <EditButton
                  className="summary"
                  from="nutrients"
                  onClick={() => {
                    protocolsRouter.goToProtocolsEditPage(
                      createSearchParams({
                        protocol_id: protocolId,
                        type: "nutrient",
                      }).toString(),
                      protocolId,
                      "products"
                    );
                  }}
                />
              </Flex>
              <Flex
                vertical
                gap={24}
                justify="start"
                className="summary-details-info-container"
              >
                <div className="">
                  <Row className="products-header">
                    <Col span={6}>
                      <p className="summary-card-static-text-seeds">
                        {formatTranslation(
                          t("createProtocol.products.nutrients.header")
                        )}
                      </p>
                    </Col>
                    <Col span={6}>
                      <p className="summary-card-static-text-seeds">
                        {formatTranslation(
                          t("createProtocol.summary.nutrients.applicationRange")
                        )}
                      </p>
                    </Col>
                    <Col span={6}>
                      <p className="summary-card-static-text-seeds">
                        {formatTranslation(
                          t("createProtocol.summary.nutrients.minDistanceUnit")
                        )}
                      </p>
                    </Col>
                    <Col span={6}>
                      <p className="summary-card-static-text-seeds">
                        {formatTranslation(
                          t("createProtocol.summary.nutrients.noOfIntervals")
                        )}
                      </p>
                    </Col>
                  </Row>

                  {data?.product_details?.nutrients && (
                    <>
                      <Row className="products-row">
                        <Col span={6}>
                          <p className="summary-card-dynamic-protocol-text">
                            {findNutrientLabel(
                              data?.product_details?.nutrients?.products[0]
                                ?.nutrient_code
                            )}
                          </p>
                        </Col>
                        <Col span={6}>
                          <p className="summary-card-dynamic-protocol-text">
                            {
                              data?.product_details?.nutrients
                                ?.application_range?.min
                            }
                            % min -{" "}
                            {
                              data?.product_details?.nutrients
                                ?.application_range?.max
                            }
                            % max
                          </p>
                        </Col>
                        <Col span={6}>
                          <p className="summary-card-dynamic-protocol-text">
                            {
                              data?.product_details?.nutrients
                                ?.interval_distance
                            }
                            %
                          </p>
                        </Col>
                        <Col span={6}>
                          <p className="summary-card-dynamic-protocol-text">
                            {
                              data?.product_details?.nutrients
                                ?.number_of_intervals
                            }
                          </p>
                        </Col>
                      </Row>
                    </>
                  )}
                </div>
                <p className="summary-nutrient-disclaimer">
                  &quot;I expect the{" "}
                  <span className="disclaimer-blue">
                    {findNutrientLabel(
                      data?.product_details?.nutrients?.products[0]
                        ?.nutrient_code ?? ""
                    )}
                  </span>{" "}
                  application, based on the grower&apos;s standard rate, to
                  range from{" "}
                  <span className="disclaimer-blue">
                    {(() => {
                      const min =
                        data?.product_details?.nutrients?.application_range
                          ?.min;

                      return min ? `${min}%` : "_";
                    })()}
                  </span>{" "}
                  to{" "}
                  <span className="disclaimer-blue">
                    {(() => {
                      const max =
                        data?.product_details?.nutrients?.application_range
                          ?.max;

                      return max ? `${max}%` : "_";
                    })()}
                  </span>
                  , in increments of at least{" "}
                  <span className="disclaimer-blue">
                    {data?.product_details?.nutrients?.interval_distance}%
                  </span>
                  , and I expect at least{" "}
                  <span className="disclaimer-blue">
                    {data?.product_details?.nutrients?.number_of_intervals === 0
                      ? "_"
                      : data?.product_details?.nutrients?.number_of_intervals}
                  </span>{" "}
                  distinct application rates.&quot;
                </p>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </div>
  );
};

export default SummaryComponent;
